import t from '@jetshop/intl';
import React, { useMemo, useContext } from 'react';
import { styled } from 'linaria/react';
import CategoryHeader from '../CategoryPage/CategoryHeader';
import MaxWidth from '../Layout/MaxWidth';
import CategoryHeaderLoadingState from './CategoryHeaderLoadingState';
import Filters from './Filters';
import ProductGridWindow from './ProductGridWindow';
import routeQuery from '../RouteQuery.gql';
import useInfinitePagination from '@jetshop/core/hooks/useInfinitePagination';
import { useLocation } from 'react-router';
import { TrendLink } from '../ui/Button';
import qs from 'qs';
import { theme } from '../Theme';
import {
  allContentComponents,
  seoComponents
} from '../ContentComponents/ContentComponents';
import { ContentRenderer } from '@jetshop/ui/ContentRenderer';
import { SeoInnerWrapper } from '../../r3-lib/components/ce/SeoRowItem/SeoRowItem';
import { Link } from 'react-router-dom';
import { FAQ, FAQItem } from '../ContentComponents/FAQ';
import ChannelContext from '@jetshop/core/components/ChannelContext';
import { cx } from 'linaria';
import DynamicCategoryRenderer from '../../r3-lib/components/layout/DynamicCategoryRenderer/DynamicCategoryRenderer';
import { default as Breadcrumbs } from '@jetshop/ui/Breadcrumbs';

const Container = styled(MaxWidth)`
  padding: 2rem 0;
  ${theme.below.lg} {
    padding: 2rem 20px;
  }
`;

const CategoryWrapper = styled('div')`
  .gradient {
    position: relative;

    ::after {
      position: absolute;
      top: 0;
      bottom: 0;
      left: auto;
      right: 0;
      background: rgb(255, 255, 255);
      background: linear-gradient(
        90deg,
        rgba(255, 255, 255, 0) 30%,
        rgba(255, 255, 255, 1) 100%
      );
      height: 100%;
      width: 40px;
      content: '';
    }

    ::before {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: auto;
      background: rgb(255, 255, 255);
      background: linear-gradient(
        90deg,
        rgba(255, 255, 255, 1) 0%,
        rgba(255, 255, 255, 0) 70%
      );
      height: 100%;
      width: 40px;
      content: '';
    }
  }
`;

const ProductNumber = styled('p')`
  font-family: ${theme.fonts.primary};
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #000;
  letter-spacing: 0.5px;
  margin: 10px 0;
  text-align: center;
`;

const ButtonWrapper = styled('div')`
  width: 100%;
  max-width: 8rem;
  margin: 0 auto;

  a {
    background-color: ${theme.colors.primary} !important;
    color: #fff !important;
    font-weight: 700 !important;
    font-family: ${theme.fonts.primary} !important;
    font-size: 12px !important;
    line-height: 17px !important;
    letter-spacing: 0.1em !important;
    border-radius: 44px !important;
    height: 33px !important;
    text-transform: uppercase !important;
  }

  &.previous {
    margin-bottom: 30px;
  }
`;

const BottomCategoryWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: 40px;
  max-width: 700px;
  margin: auto;

  ${theme.below.lg} {
    margin: 0 20px;
  }
`;

const GraphWrapper = styled('div')`
  width: calc(100% / 3);
  margin: 20px auto;
`;

const GraphBar = styled('div')`
  height: 3px;
  background: #33333333;
  position: relative;
  width: 100%;
  &::before {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    background: ${theme.colors.primary};
    left: 0;
    height: 100%;
    width: ${props => Math.round(props.fill)}%;
  }
`;

const SubCategoryWrapper = styled('div')`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: 40px auto 20px auto;
  max-width: 71rem;

  a {
    text-decoration: none;
    color: ${theme.colors.secondary};
    font-size: 14px;
    line-height: 22px;
    font-weight: 600;
    letter-spacing: 0.02em;
    margin: 4px;
    padding: 8px 14px;
    background: ${theme.colors.blackish};
    border-radius: 44px;
  }

  a:hover {
    color: ${theme.colors.blackish};
    background: ${theme.colors.secondary};
  }

  ${theme.below.lg} {
    overflow-x: scroll;
    ::-webkit-scrollbar {
      display: none;
    }
    flex-wrap: nowrap;
    margin: 0;
    padding: 0 20px 10px 20px;
    justify-content: flex-start;
    a {
      width: 100%;
      min-width: max-content;
      padding: 8px 14px;
    }
  }

  &.Onlinecykel {
    a {
      color: #fff;
      background: #c50a0a;
    }

    a:hover {
      opacity: 0.9;
    }
  }
  &.XLCykel {
    a {
      color: ${theme.colors.blackish};
      background: #fd0;
    }

    a:hover {
      background: ${theme.colors.blackish};
      color: #fd0;
    }
  }
  &.Sandnas {
    a {
      color: #e0b771;
      background: #343434;
    }

    a:hover {
      background: #e0b771;
      color: #343434;
    }
  }
`;

// const StyledBreadcrumbs = styled(Breadcrumbs)`
//   margin-bottom: 8px;
// `;

// const ContentContainer = styled(MaxWidth)`
//   padding: 60px 0;

//   .titlewrapper {
//     width: 100%;
//     justify-content: flex-start;
//     text-align: left;
//   }

//   .sidebyside {
//     max-width: 73rem;
//   }

//   ${theme.below.lg} {
//     padding: 60px 20px;
//   }

//   .item {
//     max-width: 25%;
//   }
// `;

const StatusBar = ({ percentage }) => {
  return (
    <GraphWrapper>
      <GraphBar fill={percentage} />
    </GraphWrapper>
  );
};

const WindowedCategory = ({ category, result }) => {
  const { previous, next, products } = useInfinitePagination({
    result,
    query: routeQuery
  });

  const nowShowing =
    result?.variables?.offset + (category?.products?.result?.length ?? 0);

  const totalResults = category?.products?.totalResults;
  const hasProducts = category?.products?.result?.length > 0;
  const hasSubcategories = category?.subcategories?.length > 0;

  const location = useLocation();
  const params = qs.parse(location.search, { ignoreQueryPrefix: true });
  const routePath = category?.isDynamic ? null : result?.data?.route?.path;
  const categoryPath = useMemo(() => routePath && { path: routePath }, [
    routePath
  ]);

  const { selectedChannel } = useContext(ChannelContext);

  // console.log('category', category);
  // console.log('category', category?.id === 543);
  // const breadcrumbs = result?.data?.route?.breadcrumbs;
  // const parents = result?.data?.route?.parents;

  // const breadcrumbProps = {
  //   breadcrumbs,
  //   parents
  // };

  if (!category) {
    return (
      <CategoryWrapper>
        <CategoryHeaderLoadingState />
      </CategoryWrapper>
    );
  }

  // if (category?.id === 543) {
  //   return (
  //     <ContentContainer>
  //       <StyledBreadcrumbs {...breadcrumbProps} />
  //       <DynamicCategoryRenderer
  //         categoryId={543}
  //         rendererComponents={allContentComponents}
  //       />
  //     </ContentContainer>
  //   );
  // }

  let renderHeader = true;
  if (category.data) {
    if (category.data.items.find(item => item.type === 'CategoryHeader')) {
      renderHeader = false;
    }
  }

  let renderSeoText = true;
  if (category.data) {
    if (category.data.items.find(item => item.type === 'Seo')) {
      renderSeoText = false;
    }
  }

  let renderAccordionField = false;
  if (category.data) {
    if (category.data.items.find(item => item.type === 'FAQ')) {
      renderAccordionField = true;
    }
  }

  const filteredComponents = category?.data?.items.filter(
    item => item.type !== 'FAQ'
  );

  return (
    <CategoryWrapper>
      {filteredComponents?.length > 0 ? (
        <div>
          <ContentRenderer
            items={filteredComponents}
            components={allContentComponents}
          />
        </div>
      ) : (
        <>
          {renderHeader && (
            <CategoryHeader
              category={category}
              parents={result?.data?.route?.parents}
              breadcrumbs={result?.data?.route?.breadcrumbs}
            />
          )}
        </>
      )}
      {hasSubcategories && (
        <div className="gradient">
          <SubCategoryWrapper className={cx(selectedChannel?.name)}>
            {category?.subcategories?.map(subCategory => {
              return (
                <Link to={subCategory?.primaryRoute?.path}>
                  {subCategory?.name}
                </Link>
              );
            })}
          </SubCategoryWrapper>
        </div>
      )}
      {hasProducts > 0 && (
        <Container>
          <div>
            <Filters
              filters={category.products?.filters || []}
              sortOrders={category.products?.sortOrders || []}
              result={result}
              totalProducts={totalResults}
            />
          </div>

          {previous.hasProducts && (
            <ButtonWrapper className="previous">
              <TrendLink
                className="secondary"
                onClick={e => {
                  e.preventDefault();
                  previous.fetchProducts();
                }}
                to={{
                  ...location,
                  search: qs.stringify({
                    ...params,
                    page: previous.page
                  })
                }}
              >
                {t('Show more')}
              </TrendLink>
            </ButtonWrapper>
          )}

          <ProductGridWindow
            id="category"
            prevOffset={previous.offset}
            products={products}
            listName={category.name}
            categoryPath={categoryPath}
            category={result?.data?.route?.breadcrumbs?.join('/') || ''}
            loading={result.loading}
            imageSizes={[1 / 2, 1 / 2, 1 / 3, 1 / 4]}
          />
          {next.hasProducts && (
            <>
              <ProductNumber>
                Visar {nowShowing} av {totalResults} st artiklar
              </ProductNumber>
              <ButtonWrapper>
                <TrendLink
                  className="secondary"
                  onClick={e => {
                    e.preventDefault();
                    next.fetchProducts();
                  }}
                  to={{
                    ...location,
                    search: qs.stringify({
                      ...params,
                      page: next.page
                    })
                  }}
                >
                  {t('Show more')}
                </TrendLink>
              </ButtonWrapper>
              <StatusBar percentage={(nowShowing / totalResults) * 100} />
            </>
          )}
        </Container>
      )}
      <BottomCategoryWrapper>
        {renderSeoText ? (
          category?.content && (
            <SeoInnerWrapper
              data-testid="category-description"
              dangerouslySetInnerHTML={{
                __html: category?.content
              }}
            />
          )
        ) : (
          <ContentRenderer
            items={category?.data?.items}
            components={seoComponents}
          />
        )}
        {renderAccordionField && (
          <ContentRenderer
            items={category?.data?.items}
            components={{
              FAQ: FAQ,
              FAQITEM: FAQItem
            }}
          />
        )}
      </BottomCategoryWrapper>
    </CategoryWrapper>
  );
};

export default WindowedCategory;
